import { Money, SummaryDisplayItem, AddonItem } from "../../../types/cartTypes";

import { defaultSupportedLocales } from "./basketOrderSummaryTheme";

/* ---------- Localization ---------- */

export const getLocalizedName = (
  localizedNameObj: Record<string, string> | null | undefined,
  supportedLocales: string[] = defaultSupportedLocales
) => {
  if (!localizedNameObj || typeof localizedNameObj !== "object") return "";
  for (const locale of supportedLocales) {
    if (localizedNameObj[locale]) return localizedNameObj[locale];
  }
  return Object.values(localizedNameObj)[0] || "";
};

/* ---------- Money Safety ---------- */

export const safeMoney = (money: Money | { centAmount: number; currencyCode: string } | number | null | undefined, fallbackCurrency = "USD"): Money => {
  if (typeof money === 'number') {
    return { centAmount: money, currencyCode: fallbackCurrency };
  }
  return {
    centAmount: typeof (money as Money)?.centAmount === "number" ? (money as Money).centAmount : 0,
    currencyCode: (money as Money)?.currencyCode || fallbackCurrency,
  };
};

export const extractMoney = (li: SummaryDisplayItem | null | undefined, defaultCurrency = "USD"): Money => {
  if (!li) return safeMoney(null, defaultCurrency);

  if (li.price?.value)
    return safeMoney(li.price.value, defaultCurrency);

  if (li.totalPrice)
    return safeMoney(li.totalPrice, defaultCurrency);

  // Handle case where price might be directly on the item (legacy or flattened)
  const legacyPrice = (li as unknown as Record<string, unknown>).price;
  if (legacyPrice && typeof legacyPrice === 'object' && 'centAmount' in (legacyPrice as Record<string, unknown>))
    return safeMoney(legacyPrice as Money, defaultCurrency);

  return safeMoney(null, defaultCurrency);
};

export const unwrapMoneyInput = (m: Money | { value: Money } | { centAmount: number; currencyCode: string } | null | undefined): Money | undefined => {
  if (!m) return undefined;
  if ('value' in m && m.value?.centAmount != null) return m.value as Money;
  if ('centAmount' in m && m.centAmount != null) return m as Money;
  return undefined;
};

/* ---------- Subtotal ---------- */

export const calculateEffectiveSubtotal = (
  lineItems: SummaryDisplayItem[],
  subtotalUnwrapped: Money | null | undefined,
  subtotalPriceUnwrapped: Money | null | undefined,
  currency: string,
  defaultCurrency: string,
  extractMoneyFn: (li: SummaryDisplayItem, defaultCurrency: string) => Money
): Money => {
  if (subtotalUnwrapped)
    return safeMoney(subtotalUnwrapped, currency || defaultCurrency);

  if (subtotalPriceUnwrapped)
    return safeMoney(subtotalPriceUnwrapped, currency || defaultCurrency);

  return lineItems.reduce(
    (acc: Money, li: SummaryDisplayItem) => {
      const base = extractMoneyFn(li, defaultCurrency);
      acc.centAmount += base.centAmount * (li.quantity || 1);

      const addons = li.addons || li.custom?.fields?.addons;
      if (Array.isArray(addons)) {
        addons.forEach((addon: AddonItem) => {
          const m = addon.total || addon.price;
          const addonMoney = safeMoney(m as Money | { centAmount: number; currencyCode: string }, acc.currencyCode);
          acc.centAmount += addonMoney.centAmount * (li.quantity || 1);
        });
      }
      return acc;
    },
    { centAmount: 0, currencyCode: currency || defaultCurrency }
  );
};
